import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { forwardRef } from 'react';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';

interface DispatchUpdatesBannerProps {
  isApplying: boolean;
  isQueueCapped: boolean;
  onApply: () => void;
  onDismiss: () => void;
  updatesCount: number;
}

const DispatchUpdatesBanner = forwardRef<HTMLDivElement, DispatchUpdatesBannerProps>(
  ({ isApplying, isQueueCapped, onApply, onDismiss, updatesCount }, ref) => {
    const theme = useTheme();
    const updatesLabel = isQueueCapped
      ? t('dispatch.smart_refresh.many_updates_available')
      : t('dispatch.smart_refresh.updates_available', { count: updatesCount });

    return (
      <Box
        ref={ref}
        role="status"
        aria-live="polite"
        sx={{
          alignItems: 'center',
          bgcolor: theme.brandV2.colors.treadGray10,
          borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          display: 'flex',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          {updatesLabel}
        </Typography>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <SmallLoadingButton
            color="brandV2Green"
            disabled={isApplying}
            loading={isApplying}
            onClick={onApply}
            size="small"
            variant="contained"
          >
            {t('dispatch.smart_refresh.apply_now')}
          </SmallLoadingButton>
          <SmallButton
            color="brandV2Black"
            disabled={isApplying}
            onClick={onDismiss}
            size="small"
            variant="outlined"
            // brandV2Black is a fixed navy (invisible on the dark banner);
            // flipping tokens keep it readable in both modes.
            sx={{ color: 'text.primary', borderColor: 'divider' }}
          >
            {t('dispatch.smart_refresh.dismiss')}
          </SmallButton>
        </Box>
      </Box>
    );
  },
);

DispatchUpdatesBanner.displayName = 'DispatchUpdatesBanner';

export default DispatchUpdatesBanner;
